// AGC: CategoryAccordion - Collapsible category group for inputs list
// AGC: Shows category header with icon, item count, and aggregate status
// AGC: Expands to show table of items within the category

import React, { useState, useMemo } from 'react'
import { ChevronDown, ChevronRight, AlertTriangle, Check, X, TrendingUp, Plus, Eye } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from '../../hooks/useTranslation'
import { 
  type InputCategory, 
  type StockStatus, 
  type InputCategoryInfo,
  getInputCategoryInfo 
} from '../../types/inventory'

// AGC: Smart number formatter — no trailing .0 for whole numbers
const formatQty = (n: number): string => {
  if (!isFinite(n)) return '0'
  if (Math.abs(n) >= 100) return Math.round(n).toString()
  if (Number.isInteger(n)) return n.toString()
  return n.toFixed(Math.abs(n) < 10 ? 2 : 1).replace(/\.?0+$/, '')
}

// =============================================================================
// AGC: TYPES
// =============================================================================

export interface CategoryInputItem {
  inputId: string
  name: string
  category: InputCategory
  unit: string
  totalRequired: number
  inStock: number
  needToBuy: number
  status: StockStatus
  tasks: string[]
  lotsNeeding: { id: string; name: string; required: number }[]
  hasPendingOrder?: boolean
  pendingOrderQty?: number
}

interface CategoryAccordionProps {
  category: InputCategory
  items: CategoryInputItem[]
  isExpanded: boolean
  onToggle: () => void
  onItemClick?: (item: CategoryInputItem) => void
  onAddToOrder?: (item: CategoryInputItem) => void
  onViewOrder?: (item: CategoryInputItem) => void
}

// =============================================================================
// AGC: STATUS HELPERS
// =============================================================================

const getStatusIcon = (status: StockStatus) => {
  switch (status) {
    case 'out':
    case 'critical':
      return <X className="w-3 h-3" />
    case 'low':
      return <AlertTriangle className="w-3 h-3" />
    case 'ok':
      return <Check className="w-3 h-3" />
    case 'overstock':
      return <TrendingUp className="w-3 h-3" />
    default:
      return null
  }
}

const getStatusStyles = (status: StockStatus): { bg: string; text: string } => {
  switch (status) {
    case 'out':
      return { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400' }
    case 'critical':
      return { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-600 dark:text-red-400' }
    case 'low':
      return { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-400' }
    case 'ok':
      return { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-400' }
    case 'overstock':
      return { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-400' }
    default:
      return { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-700 dark:text-gray-400' }
  }
}

// =============================================================================
// AGC: MAIN COMPONENT
// =============================================================================

export const CategoryAccordion: React.FC<CategoryAccordionProps> = ({
  category,
  items,
  isExpanded,
  onToggle,
  onItemClick,
  onAddToOrder,
  onViewOrder,
}) => {
  const { t } = useTranslation()
  const catInfo = getInputCategoryInfo(category)
  const CategoryIcon = catInfo?.iconComponent
  
  // AGC: Calculate aggregate stats for category header
  const stats = useMemo(() => {
    const critical = items.filter(i => i.status === 'out' || i.status === 'critical').length
    const low = items.filter(i => i.status === 'low').length
    const ok = items.filter(i => i.status === 'ok' || i.status === 'overstock').length
    const withOrders = items.filter(i => i.hasPendingOrder).length
    
    // AGC: Determine worst status for header badge
    let worstStatus: StockStatus = 'ok'
    if (critical > 0) worstStatus = 'critical'
    else if (low > 0) worstStatus = 'low'
    
    return { critical, low, ok, withOrders, worstStatus, total: items.length }
  }, [items])

  // AGC: Get header background based on worst status
  const headerBgClass = stats.worstStatus === 'critical' 
    ? 'bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800' 
    : stats.worstStatus === 'low'
    ? 'bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-800'
    : 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700'

  return (
    <div className="mb-2">
      {/* AGC: Category Header - Clickable to expand/collapse */}
      <button
        onClick={onToggle}
        className={`w-full flex items-center justify-between p-3 rounded-xl border transition-all ${headerBgClass} hover:shadow-sm`}
      >
        <div className="flex items-center gap-3">
          {/* AGC: Expand/Collapse chevron */}
          <div className="text-gray-400">
            {isExpanded ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </div>
          
          {/* AGC: Category icon */}
          {CategoryIcon && (
            <div 
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${catInfo?.color}20` }}
            >
              <CategoryIcon 
                className="w-4 h-4" 
                style={{ color: catInfo?.color }} 
              />
            </div>
          )}
          
          {/* AGC: Category name and count */}
          <div className="text-left">
            <span className="font-medium text-sm text-gray-900 dark:text-white">
              {catInfo?.namePt || category}
            </span>
            <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
              ({stats.total} {stats.total === 1 ? 'item' : 'itens'})
            </span>
          </div>
        </div>
        
        {/* AGC: Status badges */}
        <div className="flex items-center gap-2">
          {stats.critical > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">
              <X className="w-2.5 h-2.5" />
              {stats.critical}
            </span>
          )}
          {stats.low > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
              <AlertTriangle className="w-2.5 h-2.5" />
              {stats.low}
            </span>
          )}
          {stats.ok > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400">
              <Check className="w-2.5 h-2.5" />
              {stats.ok}
            </span>
          )}
          {stats.withOrders > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400">
              {t('inputs.ordered')}
            </span>
          )}
        </div>
      </button>
      
      {/* AGC: Expanded content - Table of items */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="mt-1 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              {/* AGC: Table Header — "Estoque" not "Em Estoque", status moved right, action col removed */}
              <div className="grid grid-cols-12 gap-1 px-3 py-2 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700 text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                <div className="col-span-4">{t('inputs.inputName')}</div>
                <div className="col-span-2 text-right">{t('inputs.required')}</div>
                <div className="col-span-2 text-right">{t('inventory.estoque') || 'Estoque'}</div>
                <div className="col-span-4 text-right">{t('inputs.status')}</div>
              </div>

              {/* AGC: Table Body */}
              {items.map((item, idx) => {
                const statusStyles = getStatusStyles(item.status)

                return (
                  <div
                    key={item.inputId}
                    className={`grid grid-cols-12 gap-1 px-3 py-2 items-center text-xs border-b border-gray-100 dark:border-gray-700/50 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors ${
                      item.status === 'out' || item.status === 'critical'
                        ? 'bg-red-50/50 dark:bg-red-900/10'
                        : item.status === 'low'
                        ? 'bg-amber-50/50 dark:bg-amber-900/10'
                        : ''
                    }`}
                  >
                    {/* AGC: Item name */}
                    <button
                      onClick={() => onItemClick?.(item)}
                      className="col-span-4 text-left truncate text-gray-900 dark:text-white font-medium hover:text-emerald-600 dark:hover:text-emerald-400"
                      title={item.name}
                    >
                      {item.name}
                    </button>

                    {/* AGC: Required quantity (smart rounding) */}
                    <div className="col-span-2 text-right text-gray-600 dark:text-gray-400">
                      {formatQty(item.totalRequired)} <span className="text-[10px]">{item.unit}</span>
                    </div>

                    {/* AGC: In stock (smart rounding) */}
                    <div className={`col-span-2 text-right font-medium ${
                      item.inStock > 0
                        ? 'text-gray-900 dark:text-white'
                        : 'text-red-600 dark:text-red-400'
                    }`}>
                      {formatQty(item.inStock)} <span className="text-[10px] font-normal text-gray-500">{item.unit}</span>
                    </div>

                    {/* AGC: Status badge + "+" button (right edge) */}
                    {/* AGC: Status pill + always-visible "+" button. Status has fixed left position
                        (flex-1 on its container) so it aligns across rows regardless of pill width. */}
                    <div className="col-span-4 flex items-center justify-end gap-1.5">
                      <div className="flex-1 flex justify-end pr-1">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-semibold ${statusStyles.bg} ${statusStyles.text}`}>
                          {t(`inputs.stockStatus.${item.status}`)}
                        </span>
                      </div>
                      {item.hasPendingOrder ? (
                        <button
                          onClick={() => onViewOrder?.(item)}
                          className="flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-full bg-purple-500 text-white hover:bg-purple-600 active:scale-95 transition-all shadow-sm"
                          aria-label={t('inputs.viewOrder')}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      ) : (
                        <button
                          onClick={() => onAddToOrder?.(item)}
                          className="flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-full bg-emerald-500 text-white hover:bg-emerald-600 active:scale-95 transition-all shadow-sm"
                          aria-label={t('inputs.addToOrder')}
                        >
                          <Plus className="w-4 h-4" strokeWidth={3} />
                        </button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default CategoryAccordion
